import React from "react";
import { motion } from "framer-motion";
import { TID } from "@/lib/testIds";
import { ShieldCheck, FileText, ScanEye, Trophy, Scale, Lock, Coins, Globe } from "lucide-react";
import { useT } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];
const ICONS = [ShieldCheck, FileText, ScanEye, Trophy, Scale, Lock, Coins, Globe];
const SPANS = [
    "md:col-span-2 md:row-span-2",
    "md:col-span-2",
    "md:col-span-2",
    "md:col-span-2",
    "md:col-span-2",
    "md:col-span-2",
    "md:col-span-2",
    "md:col-span-2",
];

export default function Features() {
    const { t } = useT();
    const items = t("features.items") || [];
    return (
        <section data-testid={TID.features.root} className="bg-sn-paper py-24 md:py-40">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue">
                        {t("features.eyebrow")}
                    </span>
                    <span className="hairline flex-1" />
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-ink-2">
                        {t("features.count")}
                    </span>
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight max-w-3xl text-sn-ink"
                >
                    {t("features.headline_a")}{" "}
                    <em className="italic text-sn-blue">
                        {t("features.headline_b")}
                    </em>
                    .
                </motion.h2>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-4 auto-rows-[220px] md:auto-rows-[240px] gap-4">
                    {items.map((f, i) => {
                        const Icon = ICONS[i] || Camera;
                        return (
                            <motion.div
                                key={i}
                                data-testid={TID.features.card(i + 1)}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.25 }}
                                transition={{ duration: 0.9, delay: i * 0.06, ease: EASE }}
                                className={`group relative p-6 md:p-8 rounded-md bg-white border border-black/10 overflow-hidden hover:border-sn-blue transition-colors duration-500 ${SPANS[i] || ""}`}
                            >
                                <div className="absolute -top-16 -right-16 w-40 h-40 bg-sn-blue/0 group-hover:bg-sn-blue/10 rounded-full blur-2xl transition-colors duration-700" />
                                <div className="relative flex flex-col h-full">
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono-e text-[10px] tracking-[0.3em] text-sn-ink-2 uppercase">
                                            0{i + 1}
                                        </span>
                                        <Icon size={22} className="text-sn-blue" strokeWidth={1.5} />
                                    </div>
                                    <div className="mt-auto">
                                        <h3 className="font-serif text-2xl md:text-3xl leading-tight text-sn-ink">
                                            {f.title}
                                        </h3>
                                        <p className="mt-3 text-sm md:text-base text-sn-ink-2 leading-relaxed max-w-md">
                                            {f.text}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
